// ─────────────────────────────────────────────────────────────────────────────
// desktopSwipeDrag — pure logic backing the interactive "drag to switch
// desktops" gesture (see DesktopSwipeDragLayer for the wheel-event wiring and
// visual layer). Unlike the old desktopSpaceSwipe.ts burst-detector this
// never silently swallows a gesture behind a large threshold — every
// qualifying horizontal wheel tick is meant to move the screen live, and only
// the *release* decision (commit vs. cancel) is threshold-gated.
// ─────────────────────────────────────────────────────────────────────────────

export type SwipeDirection = 'next' | 'previous';

/**
 * Resolves the desktop to switch to for a given swipe direction along the
 * recency-ordered list. Deliberately does NOT wrap around at either end —
 * the most-recent desktop has nothing "previous" to it, and the
 * least-recent has nothing "next", so swiping past either end must be a
 * no-op rather than cycling to the opposite end. Returns null whenever
 * there's nothing to switch to (current id not found, only desktop, or
 * already at the boundary in this direction) — callers must not start a
 * drag in that case, since there'd be nothing to reveal.
 */
export function computeSwipeTargetId(
  orderedIds: string[],
  currentId: string,
  direction: SwipeDirection,
): string | null {
  if (orderedIds.length < 2) return null;
  const currentIndex = orderedIds.indexOf(currentId);
  if (currentIndex === -1) return null;

  const offset = direction === 'next' ? 1 : -1;
  const nextIndex = currentIndex + offset;
  if (nextIndex < 0 || nextIndex >= orderedIds.length) return null;
  return orderedIds[nextIndex];
}

// Minimum accumulated |deltaX| before a gesture starts visibly dragging the
// screen — paired with the deltaX-vs-deltaY ratio check below (SWIPE_DRAG_AXIS_RATIO)
// to confirm real horizontal intent. Large enough to reject the incidental
// horizontal jitter a normal two-finger rest/scroll/adjust produces (which
// used to arm the gesture at just 4px), while still small relative to
// SWIPE_DRAG_COMMIT_FRACTION's viewport-width threshold so a real swipe still
// starts moving the screen almost immediately.
export const SWIPE_DRAG_START_PX = 28;

// How much larger |deltaX| must be than |deltaY| for a wheel tick to count as
// horizontal intent, rather than a merely-not-quite-vertical one. A bare
// majority (deltaX only fractionally bigger than deltaY) lets an ordinary
// vertical scroll with a slight diagonal wobble arm the whole-screen slide —
// this ratio requires a much more deliberately horizontal gesture.
export const SWIPE_DRAG_AXIS_RATIO = 1.75;

// No horizontal wheel activity for this long means the physical gesture has
// lifted — wheel events carry no explicit "gesture end" signal, so an idle
// gap is the only way to detect release. Same value the old burst-detector
// used for its own idle/burst boundary.
export const SWIPE_DRAG_IDLE_MS = 120;

// Fraction of the viewport width that must be dragged before release commits
// the switch instead of springing back.
export const SWIPE_DRAG_COMMIT_FRACTION = 0.42;

// A release velocity at or above this (px/ms, in the drag's own direction)
// commits the switch even short of the distance threshold — preserves the
// old "quick flick" feel without requiring a slow, deliberate drag. Also
// gates isConfidentFlick's mid-drag early commit, so this doubles as "how
// decisive a flick must be" to skip waiting for release entirely.
export const SWIPE_DRAG_COMMIT_VELOCITY_PX_MS = 0.9;

// Duration of the settle tween that finishes a commit (→ full cover) or a
// cancel (→ back to zero) once the gesture lifts.
export const SWIPE_DRAG_SETTLE_MS = 260;

// How long a committed drag holds its overlay fully covering the screen
// before fading it away, giving the real (now-navigated) content a bounded
// window to mount/paint underneath — same bounded-wait philosophy as
// desktopSlideTransition's PAINT_WAIT_TIMEOUT_MS, just applied to the
// hand-off instead of the View Transition callback.
export const SWIPE_DRAG_REVEAL_HOLD_MS = 250;
export const SWIPE_DRAG_REVEAL_FADE_MS = 150;

// Marks every piece of "application" chrome the swipe-to-switch-desktop
// gesture must never arm over — the chat panel, the system bar, or
// persistent desktop chrome (dock, sidebar, quick bar). A wheel event whose
// target is inside any of these is over an application, not the bare desktop
// background — even once that content's own inner scroller has reached its
// edge, it must keep scrolling (or simply stop) rather than start dragging
// the whole screen. `.chat-wrapper` / `.river-systembar` /
// `.system-menu-portal-dropdown` are the components' own plain (non-CSS-
// module) identity classes — the last one is portaled to `document.body` (see
// SystemMenuButton.tsx), so it'd otherwise sit entirely outside the system
// bar's DOM subtree despite visually belonging to it. The rest carry a
// dedicated `data-desktop-chrome` marker for exactly this check. Blocked
// regardless of overview state — all of these can render above the overview
// backdrop (the quick bar, paired with overview, is z-index'd above it) or
// simply aren't part of the overview surface at all.
const ALWAYS_BLOCKED_CHROME_SELECTOR =
  '.chat-wrapper, .river-systembar, .system-menu-portal-dropdown, [data-desktop-chrome]';

// An open window ([data-window-id]) and the Mission-Control "window overview"
// backdrop (.space-overview) only block the gesture OUTSIDE overview mode —
// once overview is open, they together ARE the desktop surface the swipe
// should work over, exactly like the bare background (macOS Mission Control
// accepts the same swipe-between-desktops gesture while showing all windows,
// not just over the empty desktop). Every open window is fully covered by its
// own `.window-overview-shield` for the duration of overview (see
// Window.tsx/Window.scss), so there's no real scrollable app content
// underneath the cursor to protect there either.
const OVERVIEW_ONLY_CHROME_SELECTOR = '[data-window-id], .space-overview';

export function isOverDesktopChrome(target: EventTarget | null, overviewActive = false): boolean {
  // A wheel event's `target` is typed as the generic `EventTarget` — in
  // practice always an Element, but guard the cast since `Window`/`Document`
  // (no `.closest()`) are technically valid EventTargets too.
  if (!(target instanceof Element)) return false;
  if (target.closest(ALWAYS_BLOCKED_CHROME_SELECTOR)) return true;
  if (overviewActive) return false;
  return !!target.closest(OVERVIEW_ONLY_CHROME_SELECTOR);
}

/**
 * True only when a wheel tick's horizontal component is decisively larger
 * than its vertical one (by SWIPE_DRAG_AXIS_RATIO), i.e. a real horizontal
 * swipe rather than a vertical scroll with incidental diagonal drift.
 */
export function isDeliberateHorizontalIntent(deltaX: number, deltaY: number): boolean {
  return Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_DRAG_AXIS_RATIO;
}

/**
 * Trackpad pinch-to-zoom dispatches as a `wheel` event with `ctrlKey: true`
 * (browsers' synthetic signal for the gesture — there is no dedicated pinch
 * event). It's unrelated to switching desktops and must never contribute to
 * or arm the swipe-drag accumulator.
 */
export function isPinchZoomWheelEvent(e: { ctrlKey: boolean }): boolean {
  return e.ctrlKey;
}

// Same edge-of-scrollable-content check navigationPrevention.ts uses — don't
// hijack a horizontal swipe that's legitimately scrolling something (a
// horizontally-scrolling toolbar, the switcher's own card row, etc.) unless
// it's already at that content's edge in the swipe direction.
export function isBlockedByScrollableContent(target: Element | null, deltaX: number): boolean {
  let parent = target?.parentElement ?? null;
  while (parent) {
    const overflowX = window.getComputedStyle(parent).overflowX;
    if (overflowX === 'scroll' || overflowX === 'auto') {
      const { scrollLeft, scrollWidth, clientWidth } = parent;
      const atLeftEdge = scrollLeft <= 0;
      const atRightEdge = scrollLeft >= scrollWidth - clientWidth - 1;
      return (deltaX > 0 && !atRightEdge) || (deltaX < 0 && !atLeftEdge);
    }
    parent = parent.parentElement;
  }
  return false;
}

export interface SwipeOffsetSample {
  offsetPx: number;
  atMs: number;
}

/**
 * Estimates instantaneous drag velocity (px/ms, signed) from a trailing
 * window of recent {offsetPx, atMs} samples. Used only at release time to
 * decide whether a fast flick should commit short of the distance threshold.
 */
export function estimateSwipeVelocity(samples: SwipeOffsetSample[], windowMs = 100): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1];
  const cutoff = last.atMs - windowMs;
  const first = samples.find((s) => s.atMs >= cutoff) ?? samples[0];
  const dt = last.atMs - first.atMs;
  if (dt <= 0) return 0;
  return (last.offsetPx - first.offsetPx) / dt;
}

/**
 * Decides whether a just-released drag should commit (switch to the target
 * desktop) or cancel (spring back, no navigation) — either past the distance
 * threshold, or a fast-enough flick in the same direction as the drag.
 */
export function shouldCommitSwipeDrag(
  offsetPx: number,
  viewportWidthPx: number,
  velocityPxPerMs: number,
): boolean {
  if (viewportWidthPx <= 0 || offsetPx === 0) return false;
  const fraction = Math.abs(offsetPx) / viewportWidthPx;
  if (fraction >= SWIPE_DRAG_COMMIT_FRACTION) return true;
  const sameDirection = Math.sign(velocityPxPerMs) === Math.sign(offsetPx);
  return sameDirection && Math.abs(velocityPxPerMs) >= SWIPE_DRAG_COMMIT_VELOCITY_PX_MS;
}

/**
 * True the instant an in-progress (still-dragging) gesture's own trailing
 * velocity alone already reads as a confident flick — checked on every tick
 * WHILE dragging, not just at release, so a fast flick can commit and start
 * its finishing tween the moment it's recognized.
 *
 * Without this, a real trackpad flick's decaying momentum/inertia tail keeps
 * generating small same-direction wheel ticks well after the user's fingers
 * have actually lifted — each one re-arms the idle-release timer
 * (`scheduleIdleCheck`), so the gesture doesn't reach `handleRelease` until
 * the *entire* tail finally goes quiet, up to ~1s later. Visually that's the
 * screen sliding partway, appearing to hang while the tail trickles in tiny
 * increments, then snapping the rest of the way once release finally fires.
 * Committing as soon as the flick itself is unambiguous — well within its
 * initial high-velocity burst, before any momentum tail even begins — fixes
 * this: the finishing tween (`settleTo`) starts immediately, so the slide
 * reads as one continuous motion.
 *
 * Deliberately narrower than `shouldCommitSwipeDrag`'s distance-threshold
 * branch, which stays release-only — that preserves the ability to drag
 * slowly past 35% and pull back below it to cancel before releasing, an
 * affordance a slow deliberate drag never has reason to lose.
 */
export function isConfidentFlick(offsetPx: number, velocityPxPerMs: number): boolean {
  if (offsetPx === 0) return false;
  return (
    Math.sign(velocityPxPerMs) === Math.sign(offsetPx) &&
    Math.abs(velocityPxPerMs) >= SWIPE_DRAG_COMMIT_VELOCITY_PX_MS
  );
}

/** Clamps a raw drag offset to the [0, viewportWidthPx] range a single-direction drag can occupy. */
export function clampSwipeOffset(offsetPx: number, viewportWidthPx: number): number {
  if (viewportWidthPx <= 0) return 0;
  return Math.max(0, Math.min(viewportWidthPx, offsetPx));
}

/**
 * Merges the recency-ordered space list with the live active space so its
 * background/settings changes are reflected immediately without waiting for
 * a recentSpaces cache refresh. Shared by DesktopShell's switcher dropdown
 * and DesktopSwipeDragLayer's gesture target resolution so both always agree
 * on the exact same recency order.
 */
export function buildSwitcherSpaces<T extends { id: string }>(recentSpaces: T[], activeSpace: T | null): T[] {
  const list = recentSpaces.map((s) => (activeSpace && s.id === activeSpace.id ? activeSpace : s));
  if (activeSpace && !list.some((s) => s.id === activeSpace.id)) {
    list.unshift(activeSpace);
  }
  return list;
}

/**
 * Resolves which desktop to land on after `departingId` is removed from the
 * switcher (deleted or hidden) — prefers the most recently visited *other*
 * desktop. Returns `null` when none remain, meaning the caller should spin up
 * a fresh blank Desktop instead. Shared by DesktopSpaceSwitcher's delete/hide
 * and DesktopSpaceQuickBar's hide so both pick the exact same fallback.
 */
export function pickNavigateAwayTarget<T extends { id: string }>(
  candidateSpaces: T[],
  spaceVisitTimestamps: Record<string, number>,
  departingId: string,
): T | null {
  const candidates = candidateSpaces.filter((s) => s.id !== departingId);
  if (candidates.length === 0) return null;
  return [...candidates].sort(
    (a, b) => (spaceVisitTimestamps[b.id] || 0) - (spaceVisitTimestamps[a.id] || 0)
  )[0];
}
